import { useState } from 'react';
import { getUniqId } from '../../ui-helpers';
import './action-button.css';

/**
 * ActionButton component - Bouton d'action avec icône et styles avancés
 * @param {Object} props - Propriétés du composant
 * @param {string} props.variant - Variante du bouton: 'primary', 'secondary', 'success', 'warning', 'danger', 'info'
 * @param {string} props.size - Taille du bouton: 'small', 'medium', 'large'
 * @param {ReactElement} props.icon - Icône à afficher
 * @param {string} props.title - Titre principal du bouton
 * @param {string} props.subtitle - Sous-titre du bouton
 * @param {boolean} props.disabled - État désactivé
 * @param {function} props.onClick - Fonction de clic
 * @param {Object} props.style - Styles personnalisés
 * @param {boolean} props.compact - Version compacte du bouton
 * @param {boolean} props.fullWidth - Bouton qui occupe toute la largeur
 * @returns {JSX.Element} Composant ActionButton
 */
const ActionButton = (props) => {
    const {
        variant = 'primary',
        size = 'medium',
        icon,
        title,
        subtitle,
        disabled = false,
        onClick,
        style = {},
        compact = false,
        fullWidth = false,
        className = '',
        ...otherProps
    } = props;

    const [id] = useState(props?.id ? props.id : getUniqId());

    const handleClick = (e) => {
        if (!disabled && onClick) {
            onClick(e);
        }
    };

    const classes = [
        'action-button',
        `action-button--${variant}`,
        `action-button--${size}`,
        compact && 'action-button--compact',
        fullWidth && 'action-button--full-width',
        disabled && 'action-button--disabled',
        className
    ].filter(Boolean).join(' ');

    return (
        <button
            id={id}
            className={classes}
            onClick={handleClick}
            disabled={disabled}
            style={style}
            {...otherProps}
        >
            {/* Éléments décoratifs pour certaines variantes */}
            <div className="action-button__decorative-elements">
                {variant === 'primary' && (
                    <div className="action-button__chart-decoration">
                        <div className="chart-bar chart-bar--1"></div>
                        <div className="chart-bar chart-bar--2"></div>
                        <div className="chart-bar chart-bar--3"></div>
                    </div>
                )}
                {variant === 'success' && (
                    <div className="action-button__document-decoration">
                        <div className="document-line document-line--1"></div>
                        <div className="document-line document-line--2"></div>
                    </div>
                )}
                {variant === 'warning' && (
                    <div className="action-button__robot-decoration">
                        <div className="robot-head"></div>
                        <div className="robot-body"></div>
                        <div className="robot-antenna"></div>
                    </div>
                )}
                {variant === 'info' && (
                    <div className="action-button__settings-decoration">
                        <div className="settings-line">
                            <div className="settings-bar"></div>
                            <div className="settings-dot"></div>
                        </div>
                        <div className="settings-line">
                            <div className="settings-bar settings-bar--short"></div>
                            <div className="settings-dot"></div>
                        </div>
                    </div>
                )}
            </div>

            {/* Icône */}
            {icon && (
                <div className="action-button__icon">
                    {icon}
                </div>
            )}

            {/* Contenu textuel */}
            <div className="action-button__content">
                {title && (
                    <div className="action-button__title">
                        {title}
                    </div>
                )}
                {subtitle && (
                    <div className="action-button__subtitle">
                        {subtitle}
                    </div>
                )}
            </div>
        </button>
    );
};

export default ActionButton;